import React, { useCallback, useEffect, useState } from 'react';
import AppointmentModal, { AppointmentData } from './modals/AppointmentModal';
import { useSchedulePermissions } from '../hooks/useSchedulePermissions';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  schedulesService, mapScheduleError, ScheduleItem, ScheduleOptions, ScheduleSeriesItem, RescheduleSeriesPayload,
} from '../services/schedulesService';
import { scheduleBlocksService } from "../services/scheduleBlocksService";
import { patientsService } from '../services/patientsService';
import { holidaysService } from '../services/holidaysService';

/**
 * Editor de UM agendamento fora da agenda (caixa do paciente): abre o MESMO modal de edição do
 * calendário e da aba Agenda (série, histórico, exclusão, janela de edição, RN-302), carregando
 * o agendamento pelo id. Mesma leitura do gravado da aba Agenda (valores nunca recalculados).
 */
interface Props {
  scheduleId: string | null;
  patientId: string;
  patientName: string;
  entityId: string;
  onClose: () => void;
  /** Depois de salvar/excluir: quem chamou recarrega a própria tela */
  onChanged?: () => void;
}

const money = (v: number | null): string => (v == null ? '' : v.toFixed(2));
const timeOf = (iso: string): string => iso.slice(11, 16);
const endTimeForModal = (item: ScheduleItem): string => {
  const minutes = Math.round(
    (new Date(item.dateTimeFinal).getTime() - new Date(item.dateTimeInitial).getTime()) / 60000);
  const [h, m] = timeOf(item.dateTimeInitial).split(':').map(Number);
  const total = h * 60 + m + (minutes > 0 ? minutes : 30);
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

export const toAppointmentModalData = (item: ScheduleItem, patientName: string): Partial<AppointmentData> => ({
  roomId: item.roomId ?? null,
  patient: item.patientName || patientName,
  patientId: item.patientId,
  firstResponsible: '',
  secondResponsible: '',
  professional: item.professionalName || '',
  team: item.teamName || '',
  serviceType: item.serviceTypeName || '',
  serviceTypeId: item.serviceTypeId,
  startDate: item.dateTimeInitial.slice(0, 10),
  startTime: timeOf(item.dateTimeInitial),
  endDate: item.dateTimeInitial.slice(0, 10),
  endTime: endTimeForModal(item),
  appointmentType: (item.occurrences ?? 1) > 1 ? 'recurring' : 'unique',
  recurrenceType: '',
  maxOccurrences: item.occurrences ?? 1,
  unitValue: money(item.eventValueUnit),
  discount: money(item.eventValueUnitDiscount ?? 0),
  totalUnit: money(item.eventValueTotalUnit),
  totalValue: money(item.eventValueTotalAll),
  observations: item.notes || '',
  confirmationStatus: item.patientConfirmation || '',
  attendanceStatus: item.patientPresence || '',
  cancellationReason: item.reasonToCancellation || '',
});

const AppointmentEditorDialog: React.FC<Props> = ({ scheduleId, patientId, patientName, entityId, onClose, onChanged }) => {
  const { toast, showToast, hideToast } = useToast();
  const [options, setOptions] = useState<ScheduleOptions | null>(null);
  const [item, setItem] = useState<ScheduleItem | null>(null);
  const [seriesItems, setSeriesItems] = useState<ScheduleSeriesItem[]>([]);
  const [modalData, setModalData] = useState<Partial<AppointmentData>>({});

  useEffect(() => {
    if (!scheduleId) return;
    schedulesService.getOptions().then(setOptions).catch(() => showToast('Não foi possível carregar os dados da agenda.', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);

  const openById = useCallback(async (id: string) => {
    try {
      const loaded = await schedulesService.getById(id);
      setItem(loaded);
      setModalData(toAppointmentModalData(loaded, patientName));
      setSeriesItems([]);
      if ((loaded.occurrences ?? 1) > 1) {
        schedulesService.getSeries(loaded.id).then(setSeriesItems).catch(() => setSeriesItems([]));
      }
    } catch {
      showToast('Não foi possível abrir o agendamento.', 'error');
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientName]);

  useEffect(() => {
    if (scheduleId) openById(scheduleId); else { setItem(null); setModalData({}); setSeriesItems([]); }
  }, [scheduleId, openById]);

  const isOwnScope = options?.dataScope === 'own';
  const schedulePerms = useSchedulePermissions();

  const handleSave = async (data: AppointmentData) => {
    if (!item) return;
    if (!data.professionalId || !data.serviceTypeId) throw new Error('Selecione profissional e serviço.');
    if (!data.startDate || !data.startTime || !data.endTime) throw new Error('Informe a data e os horários do agendamento.');
    try {
      const result = await schedulesService.updateSchedule(item.id, {
        professionalRegistrationId: data.professionalId,
        teamId: data.teamId ?? null,
        serviceTypeId: data.serviceTypeId,
        startDate: data.startDate.slice(0, 10),
        startTime: data.startTime,
        endTime: data.endTime,
        confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
        presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
        notes: data.observations ? data.observations : null,
        cancellationReason: data.cancellationReason ? data.cancellationReason : null,
        applyToSeries: data.applyToSeries ?? false,
        roomId: data.roomId ?? '',
      });
      showToast(result.updated > 1 ? `${result.updated} agendamentos atualizados.` : 'Agendamento atualizado.', 'success');
      onChanged?.();
      await openById(item.id);
    } catch (err: any) {
      throw new Error(mapScheduleError(err));
    }
  };

  const handleDelete = async (opts: { reason: string; includeFollowing: boolean }) => {
    if (!item) return;
    try {
      const result = await schedulesService.deleteSchedule(item.id, opts);
      const kept = result.skippedProtected > 0 ? ` ${result.skippedProtected} mantido(s) por status ou pagamento.` : '';
      showToast(result.deleted > 1 ? `${result.deleted} agendamentos excluídos.${kept}` : `Agendamento excluído.${kept}`, 'success');
      onChanged?.();
    } catch (err: any) {
      throw new Error(mapScheduleError(err));
    }
  };

  // Lista da série editável (RN-118m)
  const handleRescheduleSeries = async (payload: RescheduleSeriesPayload) => {
    if (!item) return;
    try {
      const result = await schedulesService.rescheduleSeries(item.id, payload);
      const parts = [
        result.moved > 0 ? `${result.moved} ${result.moved === 1 ? 'sessão remanejada' : 'sessões remanejadas'}` : '',
        result.deleted > 0 ? `${result.deleted} ${result.deleted === 1 ? 'removida' : 'removidas'}` : '',
      ].filter(Boolean);
      showToast(`${parts.join(' e ')}.`.replace(/^./, c => c.toUpperCase()), 'success');
      setSeriesItems(await schedulesService.getSeries(item.id));
      onChanged?.();
    } catch (err: any) {
      throw new Error(mapScheduleError(err));
    }
  };

  const loadPatientDiscounts = useCallback(async () => schedulesService.getPatientDiscounts(patientId), [patientId]);
  const loadPatientInfo = useCallback(async () => {
    const patient = await patientsService.getById(patientId);
    return { responsible1: patient.responsibleName1 ?? null, responsible2: patient.responsibleName2 ?? null };
  }, [patientId]);
  const loadHolidaysOnDate = useCallback(async (date: string) => {
    if (!entityId) return [];
    const rows = await holidaysService.getOnDate(entityId, date);
    return rows.map(h => h.holidayTitle);
  }, [entityId]);
  const loadHolidayCalendar = useCallback(async () => {
    if (!entityId) return {};
    const rows = await holidaysService.getByEntity(entityId, false);
    return rows.reduce<Record<string, string>>((map, h) => { if (h.active) map[h.day] = h.holidayTitle; return map; }, {});
  }, [entityId]);

  return (
    <>
      <AppointmentModal
        isOpen={!!scheduleId && !!item}
        onClose={onClose}
        onSave={handleSave}
        mode="edit"
        initialData={modalData}
        title="Editar Agendamento"
        patientsList={[patientName]}
        businessHours={options?.businessHours}
        disablePatientField
        professionalsList={(options?.professionals ?? []).filter(p => p.active).map(p => p.name)}
        teamsList={(options?.teams ?? []).map(t => t.name)}
        serviceTypesList={(options?.serviceTypes ?? []).map(s => s.name)}
        professionalOptions={(options?.professionals ?? []).filter(p => p.active)}
        roomOptions={options?.rooms ?? []}
        teamOptions={options?.teams ?? []}
        serviceTypeOptions={options?.serviceTypes ?? []}
        loadPatientDiscounts={loadPatientDiscounts}
        loadPatientInfo={loadPatientInfo}
        loadHolidaysOnDate={loadHolidaysOnDate}
        loadBlocks={(professionalId, from, to) => scheduleBlocksService.getBlocks(from, to, professionalId)}
        loadConflicts={({ professionalId, patient, from, to }) => schedulesService.findConflicts(
          professionalId,
          (patientId || null),
          from, to)}
        loadHolidayCalendar={loadHolidayCalendar}
        confirmationOptions={(options?.confirmationStatuses ?? []).map(s => s.name)}
        presenceOptions={(options?.presenceStatuses ?? []).map(s => s.name)}
        terminalConfirmations={(options?.confirmationStatuses ?? []).filter(s => s.isTerminal).map(s => s.name)}
        showPricing={!isOwnScope}
        ownProfileLock={isOwnScope}
        paid={item?.paid ?? false}
        paymentDate={item?.paymentDate ?? null}
        paymentFinalValue={item?.paymentFinalValue ?? null}
        paymentDiscount={item?.paymentDiscount ?? null}
        seriesItems={seriesItems}
        currentScheduleId={item?.id ?? null}
        onDelete={handleDelete}
        canDelete={schedulePerms.canDelete}
        locked={(item?.locked ?? false) || !schedulePerms.canOpenForChange}
        lockedMessage={!(item?.locked ?? false) && !schedulePerms.canOpenForChange ? 'Somente leitura — seu perfil não altera agendamentos' : undefined}
        attendanceOnly={!schedulePerms.canEdit && schedulePerms.canAttendance}
        statusLocked={!schedulePerms.canAttendance}
        rescheduleCount={item?.numReschedule ?? 0}
        loadHistory={item ? () => schedulesService.getHistory(item.id) : undefined}
        onOpenOccurrence={openById}
        onRescheduleSeries={schedulePerms.canEdit ? handleRescheduleSeries : undefined}
      />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </>
  );
};

export default AppointmentEditorDialog;
